import React, { useCallback, useEffect, useState } from "react";
// @ts-ignore
import { store } from "react-notifications-component";

import countriesContinents from "../assets/data/country-continents.json";
import rateRequestLabels from "../assets/data/rate-request-labels";
import relationshipLabels from "../assets/data/relationship-labels.json";
import typeLabels from "../assets/data/type-labels.json";
import countryCodesJSON from "../assets/data/country-codes-iso3316-2.json";

import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import { config } from "../firebase.config.js";

import { ReactComponent as LoadingIcon } from "../assets/images/logo-animated.svg";

const firebaseApp = firebase.initializeApp(config);
const firestore = firebase.firestore();
const storage = firebase.storage();

const firebaseAuth = firebase.auth();

const countryCodes: StrictDict = countryCodesJSON;
const countryCodesInverted: StrictDict = {};

Object.keys(countryCodes).forEach((countryCode: string) => {
  const countryItem = countryCodes[countryCode];

  countryCodesInverted[countryItem.name] = {
    ...countryItem,
    countryCode: countryCode,
  };
});

// Run firestore locally when on localhost
// if (window.location.hostname === "localhost") {
//   firestore.settings({
//     host: "localhost:8081",
//     ssl: false,
//   });
// }

// Limit amount of rate requests on localhost
const rateRequestLimit = window.location.hostname === "localhost" ? 3000 : 3000;
const supplierRequestLimit =
  window.location.hostname === "localhost" ? 3000 : 5000;

const GlobalData = React.createContext<any>(null);

export default GlobalData;

export function GlobalDataProvider({ children }: { children: any }) {
  const [rateRequestSnapshotData, setRateRequestSnapshotData] =
    useState<any>(null);
  const [currentUser, setCurrentUser] = useState<StrictDict>({});
  const [isCurrentUserLoading, setIsCurrentUserLoading] =
    useState<boolean>(true);
  const [suppliers, setSuppliers] = useState<Array<Supplier>>([]);
  const [rateRequests, setRateRequests] = useState<Array<RateRequest>>([]);
  const [groups, setGroups] = useState<Array<Object>>([]);
  const [users, setUsers] = useState<Array<Account>>([]);

  const updateRateRequests = useCallback(
    (newRateRequests: Array<RateRequest>) => {
      // console.log('### Current rate requests saved:', rateRequests);
      // console.log('@@@ Adding new rate requests:', newRateRequests);

      setRateRequests(newRateRequests);
    },
    [rateRequests]
  );

  const getSuppliers = () => {
    // Set up suppliers snapshot only once on load
    const suppliersSnapshot = firestore
      .collection("suppliers")
      .where("status.inactive", "!=", true)
      .limit(supplierRequestLimit)
      .onSnapshot((querySnapshot: any) => {
        /**
         * Use commented code in this function to export the suppliers list
         */
        // const exportArray:Array<StrictDict> = [];

        const foundSuppliers: Array<Supplier> = querySnapshot.docs.map(
          (doc: any) => {
            const docData = {
              ...doc.data(),
              uid: doc.id,
              carId: doc.id,
            };

            return docData;

            // Add supplier to export array if it's not inactive
            // if (!docData.status || !docData.status.inactive || docData.status.inactive === false) {
            //   exportArray.push(docData);
            // }

            // const exportObj = {
            //   carId: doc.id,
            //   name: docData.car.Client,
            //   alias: "",
            //   status: "",
            //   relationship: "",
            //   agreementSigned: "",
            //   customEmail: "",
            //   types: "",
            //   groups: "",
            //   coverage: "",
            //   comment: ""
            // };

            // exportArray.push(exportObj);
          }
        );

        // Log all active suppliers to console
        // console.log(JSON.stringify(exportArray));

        // let exportString = '';

        // exportArray.forEach(supplier => exportString = exportString + `${supplier.carId};${supplier.name}\n`);

        // console.log(exportString);

        process.env.NODE_ENV === "development" &&
          console.log("Suppliers snapshot updated");

        setSuppliers(foundSuppliers);
      });

    return suppliersSnapshot;
  };

  const getInactiveSuppliersWithinLastMonth = () => {
    // Set up suppliers snapshot only once on load
    let foundSuppliers: Array<Supplier> = [];

    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const suppliersSnapshot2 = firestore
      .collection("suppliers")
      .where("status.inactive", "==", true)
      .where("createdAt", ">", monthAgo)
      .limit(supplierRequestLimit)
      .get()
      .then((querySnapshot2: any) => {
        foundSuppliers = foundSuppliers.concat(
          querySnapshot2.docs.map((doc: any) => {
            const docData = {
              ...doc.data(),
              uid: doc.id,
              carId: doc.id,
            };

            return docData;
          })
        );

        console.log("Suppliers snapshot updated");

        setSuppliers((prevState) => {
          return prevState.concat(foundSuppliers);
        });
      });

    return suppliersSnapshot2;
  };

  useEffect(() => {
    // console.log(rateRequestSnapshotData);

    const querySnapshot = rateRequestSnapshotData;

    if (!querySnapshot) {
      return;
    }

    // Initialize rate requests array with existing rate requests
    const foundRateRequests: Array<RateRequest> = [];
    const changedDocs = querySnapshot.docChanges();
    const changedDocsLength = changedDocs.length;
    const currentRateRequests = [...rateRequests];

    for (let index: number = 0; index < changedDocsLength; index++) {
      const change = changedDocs[index];

      // console.log(change);

      if (change.type === "removed") {
        currentRateRequests.splice(change.oldIndex, 1);
      }

      if (change.type === "modified") {
        currentRateRequests[change.oldIndex] = changedDocs[
          index
        ].doc.data() as RateRequest;
      }

      if (change.type === "added") {
        const doc: StrictDict = changedDocs[index].doc;
        const data = doc.data();
        data.uid = doc.id;

        if (typeof data.user === "string") {
          data.user =
            users.find((user: Account) => user.uid === data.user) || data.user;
        }

        if (change.newIndex === 0) {
          currentRateRequests.unshift(data);
        } else {
          currentRateRequests[change.newIndex] = data;
        }
      }
    }

    updateRateRequests(currentRateRequests);
  }, [rateRequestSnapshotData]);

  const getRateRequests = () => {
    // Set up suppliers snapshot only once on load
    const rateRequestSnapshot = firestore
      .collection("rate_requests")
      .limit(rateRequestLimit)
      .orderBy("createdAt", "desc");

    const unsubscribe = rateRequestSnapshot.onSnapshot((querySnapshot: any) => {
      setRateRequestSnapshotData(querySnapshot);
    });

    return unsubscribe;
  };

  const getGroups = () => {
    // Set up groups snapshot only once on load
    const groupsSnapshot = firestore
      .collection("supplier_groups")
      .orderBy("id", "asc")
      .limit(9999)
      .onSnapshot(async (querySnapshot: any) => {
        const foundGroups: Array<Object> = [];

        await Promise.all(
          querySnapshot.docs.map(async (doc: any) => {
            foundGroups.push(doc.data());
          })
        );

        setGroups(foundGroups);
      });

    process.env.NODE_ENV === "development" && console.log("Groups snapshot");

    return groupsSnapshot;
  };

  const getUsers = () => {
    // Set up users snapshot only once on load
    const usersSnapshot = firestore
      .collection("users")
      .limit(250)
      .onSnapshot((querySnapshot: any) => {
        const foundUsers: Array<Account> = querySnapshot.docs.map(
          (doc: any) => {
            const data = {
              ...doc.data(),
              uid: doc.id,
            };

            return data;
          }
        );

        setUsers(foundUsers);
      });

    process.env.NODE_ENV === "development" && console.log("Users snapshot");

    return usersSnapshot;
  };

  // Run this once on load
  useEffect(() => {
    // Snapshot unsubscribe references
    let usersSnapshotUnsub: firebase.Unsubscribe | null = null;
    let groupsSnapshotUnsub: firebase.Unsubscribe | null = null;
    let suppliersSnapshotUnsub: firebase.Unsubscribe | null = null;
    let rateRequestSnapshotUnsub: firebase.Unsubscribe | null = null;

    process.env.NODE_ENV === "development" &&
      console.log("Initial App.tsx render");

    // AUTH LISTENER (runs once)
    const unsubAuth = firebaseAuth.onAuthStateChanged(async (user: any) => {
      if (user) {
        // ------------------------------
        // USER LOGGED IN
        // ------------------------------

        // 👇 NEVER mutate React state!
        setCurrentUser({
          firebase: user,
          custom: {}, // (you can populate this later)
        });

        localStorage.setItem("expectSignIn", "true");
        if (user.emailVerified) {
          localStorage.setItem("verified", "true");
        }

        // ------------------------------
        // SET UP SNAPSHOTS
        // ------------------------------
        groupsSnapshotUnsub = getGroups();
        usersSnapshotUnsub = getUsers();
        suppliersSnapshotUnsub = getSuppliers();
        rateRequestSnapshotUnsub = getRateRequests();
      } else {
        // ------------------------------
        // USER LOGGED OUT
        // ------------------------------

        setCurrentUser((c: StrictDict) => {
          c.firebase = null;

          return c;
        });

        localStorage.removeItem("expectSignIn");
        localStorage.removeItem("verified");

        // Cleanup snapshot listeners on logout
        usersSnapshotUnsub && usersSnapshotUnsub();
        groupsSnapshotUnsub && groupsSnapshotUnsub();
        suppliersSnapshotUnsub && suppliersSnapshotUnsub();
        rateRequestSnapshotUnsub && rateRequestSnapshotUnsub();

        setUsers([]);
        setGroups([]);
        setRateRequests([]);
        setSuppliers([]);
      }

      setIsCurrentUserLoading(false);
    });

    // ------------------------------
    // useEffect CLEANUP
    // Runs on component unmount
    // ------------------------------
    return () => {
      unsubAuth();

      usersSnapshotUnsub && usersSnapshotUnsub();
      groupsSnapshotUnsub && groupsSnapshotUnsub();
      suppliersSnapshotUnsub && suppliersSnapshotUnsub();
      rateRequestSnapshotUnsub && rateRequestSnapshotUnsub();
    };
  }, []);

  // Update currentUser when users state updates
  useEffect(() => {
    if (!currentUser?.firebase?.uid) return;

    const customData = users.find(
      (u: Account) => u.uid === currentUser.firebase.uid
    );

    if (!customData) return;

    // Only update if something actually changed
    setCurrentUser((prev: StrictDict) => {
      if (prev.custom?.uid === customData.uid) return prev;

      return {
        ...prev,
        custom: customData,
      };
    });

    // If admin, load inactive suppliers
    if (customData.admin) {
      getInactiveSuppliersWithinLastMonth();

      store.addNotification({
        message:
          "Admin only: Loading inactive suppliers added within last month",
        type: "info",
        insert: "top",
        container: "top-center",
        animationIn: ["animated", "slideDown"],
        animationOut: ["animated", "slideUp"],
        dismiss: {
          pauseOnHover: true,
          duration: 10000,
        },
      });
    }
  }, [users, currentUser?.firebase?.uid]);

  const state = {
    // Labels
    countriesContinents,
    rateRequestLabels,
    relationshipLabels,
    typeLabels,
    countryCodes,
    countryCodesInverted,

    // States
    currentUser,
    setCurrentUser,
    suppliers,
    setSuppliers,
    rateRequests,
    setRateRequests,
    groups,
    setGroups,
    users,
    setUsers,

    // Firebase
    storage,
    firebaseApp,
    firestore,
    firebaseAuth,
  };

  if (isCurrentUserLoading) {
    return (
      <div className="loader loader--full">
        <LoadingIcon />
      </div>
    );
  }

  return <GlobalData.Provider value={state}>{children}</GlobalData.Provider>;
}
